/**
 * Error Codes extracted from actual domain error classes
 *
 * This object maps all error codes from domain error classes to provide
 * type-safe constants for API documentation and usage.
 *
 * Using object pattern for better performance and developer experience:
 * - Single export vs 50+ individual exports
 * - Namespaced access: CommonErrorCodes.USER_NOT_FOUND
 * - Better tree-shaking and bundle optimization
 * - Cleaner type definition
 */
export const CommonErrorCodes = {
    // User domain
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
    USER_NOT_AUTHORIZED: 'USER_NOT_AUTHORIZED',
    // Token domain
    TOKEN_NOT_FOUND: 'TOKEN_NOT_FOUND',
    TOKEN_NOT_ACTIVE: 'TOKEN_NOT_ACTIVE',
    TOKEN_ID_REQUIRED: 'TOKEN_ID_REQUIRED',
    TOKEN_TYPE_NOT_FOUND: 'TOKEN_TYPE_NOT_FOUND',
    TOKEN_UNIT_NOT_FOUND: 'TOKEN_UNIT_NOT_FOUND',
    NFT_EXPIRED: 'NFT_EXPIRED',
    NFT_BUSINESS_RESTRICTION: 'NFT_BUSINESS_RESTRICTION',
    // Wallet domain
    WALLET_MISSING_SIGNING: 'WALLET_MISSING_SIGNING',
    WALLET_NOT_FOUND: 'WALLET_NOT_FOUND',
    SMART_WALLET_PROCESSING_FAILED: 'SMART_WALLET_PROCESSING_FAILED',
    // Balance domain
    INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
    // Contract domain
    CONTRACT_NOT_FOUND: 'CONTRACT_NOT_FOUND',
    // Business domain
    BUSINESS_NOT_FOUND: 'BUSINESS_NOT_FOUND',
    BUSINESS_NOT_MINTER: 'BUSINESS_NOT_MINTER',
    // Membership domain
    MEMBERSHIP_NOT_FOUND: 'MEMBERSHIP_NOT_FOUND',
    MEMBERSHIP_ALREADY_EXISTS: 'MEMBERSHIP_ALREADY_EXISTS',
    MEMBERSHIP_LAST_OWNER: 'MEMBERSHIP_LAST_OWNER',
    // Transaction domain
    TRANSACTION_NOT_FOUND: 'TRANSACTION_NOT_FOUND',
    TRANSACTION_SIGNATURE_INVALID: 'TRANSACTION_SIGNATURE_INVALID',
    TRANSACTION_ALREADY_COMPLETED: 'TRANSACTION_ALREADY_COMPLETED',
    TRANSACTION_NOT_PENDING: 'TRANSACTION_NOT_PENDING',
    TRANSACTION_EXPIRED: 'TRANSACTION_EXPIRED',
    TRANSACTION_VALIDATION_ERROR: 'TRANSACTION_VALIDATION_ERROR',
    UNEXPECTED_VALIDATION_SCENARIO: 'UNEXPECTED_VALIDATION_SCENARIO',
    // Campaign domain
    CAMPAIGN_USER_INFO_REQUIRED: 'CAMPAIGN_USER_INFO_REQUIRED',
    CAMPAIGN_BUSINESS_REQUIRED: 'CAMPAIGN_BUSINESS_REQUIRED',
    CAMPAIGN_CONDITION_NOT_MET: 'CAMPAIGN_CONDITION_NOT_MET',
    CAMPAIGN_ALREADY_CLAIMED: 'CAMPAIGN_ALREADY_CLAIMED',
    CAMPAIGN_NO_TRIGGER: 'CAMPAIGN_NO_TRIGGER',
    CAMPAIGN_LOCATION_REQUIRED: 'CAMPAIGN_LOCATION_REQUIRED',
    CAMPAIGN_DISTANCE_EXCEEDED: 'CAMPAIGN_DISTANCE_EXCEEDED',
    CAMPAIGN_NOT_ACTIVE: 'CAMPAIGN_NOT_ACTIVE',
    CAMPAIGN_ENDED: 'CAMPAIGN_ENDED',
    CAMPAIGN_NOT_STARTED: 'CAMPAIGN_NOT_STARTED',
    CAMPAIGN_CLAIM_LIMIT_REACHED: 'CAMPAIGN_CLAIM_LIMIT_REACHED',
    CAMPAIGN_COOLDOWN_ACTIVE: 'CAMPAIGN_COOLDOWN_ACTIVE',
    CAMPAIGN_DAILY_LIMIT_REACHED: 'CAMPAIGN_DAILY_LIMIT_REACHED',
    CAMPAIGN_GLOBAL_LIMIT_REACHED: 'CAMPAIGN_GLOBAL_LIMIT_REACHED',
    CAMPAIGN_GLOBAL_DAILY_LIMIT_REACHED: 'CAMPAIGN_GLOBAL_DAILY_LIMIT_REACHED',
    CAMPAIGN_NOT_FOUND: 'CAMPAIGN_NOT_FOUND',
    GEOGRAPHIC_RESTRICTION: 'GEOGRAPHIC_RESTRICTION',
    LOCATION_VERIFICATION_REQUIRED: 'LOCATION_VERIFICATION_REQUIRED',
    // Trigger domain
    TRIGGER_NOT_FOUND: 'TRIGGER_NOT_FOUND',
    // Trigger Source domain
    TRIGGER_SOURCE_NOT_FOUND: 'TRIGGER_SOURCE_NOT_FOUND',
    // Generic domain errors
    RESOURCE_ALREADY_EXISTS: 'RESOURCE_ALREADY_EXISTS',
    RESOURCE_IN_USE: 'RESOURCE_IN_USE',
    // Technical errors
    TECHNICAL_ERROR: 'TECHNICAL_ERROR',
    ORCHESTRATION_ERROR: 'ORCHESTRATION_ERROR',
    // System errors
    SYSTEM_ERROR: 'SYSTEM_ERROR',
    EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
    AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED',
    AUTHORIZATION_FAILED: 'AUTHORIZATION_FAILED',
    CONFIGURATION_MISSING: 'CONFIGURATION_MISSING',
    // Redemption domain
    USER_STATUS_RESTRICTED: 'USER_STATUS_RESTRICTED',
    REDEMPTION_ALREADY_PROCESSING: 'REDEMPTION_ALREADY_PROCESSING',
    MAX_REDEMPTION: 'MAX_REDEMPTION',
    USER_LIMIT: 'USER_LIMIT',
    REQUIRED_FIELDS_MISSING: 'REQUIRED_FIELDS_MISSING',
    BOOKING_REQUIREMENT_NOT_MET: 'BOOKING_REQUIREMENT_NOT_MET',
    // Authentication errors
    INVALID_TOKEN: 'INVALID_TOKEN',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    TOKEN_REVOKED: 'TOKEN_REVOKED',
    REFRESH_TOKEN_EXPIRED: 'REFRESH_TOKEN_EXPIRED',
    REFRESH_TOKEN_REVOKED: 'REFRESH_TOKEN_REVOKED',
    FIREBASE_AUTH_ERROR: 'FIREBASE_AUTH_ERROR',
    MULTIPLE_CONTEXT_SELECTION_REQUIRED: 'MULTIPLE_CONTEXT_SELECTION_REQUIRED',
    ADMIN_TENANT_REQUIRED: 'ADMIN_TENANT_REQUIRED',
    // Magic link errors
    MAGIC_LINK_EMAIL_SEND_FAILED: 'MAGIC_LINK_EMAIL_SEND_FAILED',
    MAGIC_LINK_EMAIL_MISMATCH: 'MAGIC_LINK_EMAIL_MISMATCH',
    MAGIC_LINK_VERIFICATION_FAILED: 'MAGIC_LINK_VERIFICATION_FAILED',
    // Rate limiting (generic)
    RATE_LIMITED: 'RATE_LIMITED',
    // Generic fallbacks
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    BUSINESS_RULE_VIOLATION: 'BUSINESS_RULE_VIOLATION',
    RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
    // Webhook domain
    WEBHOOK_NOT_FOUND: 'WEBHOOK_NOT_FOUND',
    WEBHOOK_INACTIVE: 'WEBHOOK_INACTIVE',
    WEBHOOK_SOURCE_NOT_ALLOWED: 'WEBHOOK_SOURCE_NOT_ALLOWED',
    WEBHOOK_SIGNATURE_INVALID: 'WEBHOOK_SIGNATURE_INVALID',
    WEBHOOK_FORWARD_FAILED: 'WEBHOOK_FORWARD_FAILED',
    WEBHOOK_TIMEOUT: 'WEBHOOK_TIMEOUT',
    // Conflict errors (409)
    RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',
    CONCURRENT_MODIFICATION: 'CONCURRENT_MODIFICATION',
    // Timeout errors (504)
    REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',
    GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT'
};
// Re-export ErrorDomains from centralized entity types
export { ErrorDomains } from '../enum/entity-types.enum.js';
//# sourceMappingURL=error-codes.js.map